'use client';

import type { CSSProperties } from 'styled-components';
import { css } from 'styled-components';

import { Spacing } from '@zoomit/primitives';

import type { ButtonVariant } from '../packages/button';
import type { ColorScheme } from './../theme/button';
import { buttonBehaviorTheme, buttonTheme } from './../theme/button';

export type ButtonSize = 'full width' | 'small' | 'medium' | 'auto' | 'fit';

type Args = {
  size: ButtonSize;
  variant: ButtonVariant;
  colorScheme: ColorScheme;
  disabled?: boolean;
  fontWeight?: CSSProperties['fontWeight'];
};

export const linkButton = (args: Args) => {
  const {
    size = 'auto',
    variant = 'contained',
    colorScheme = 'default',
    disabled = false,
    fontWeight,
  } = args;

  return css`
    border-radius: ${() => (variant === 'text' ? 0 : 16)}px;
    font-family: inherit;
    font-size: 1.2rem;

    font-weight: ${() => {
      if (fontWeight) return fontWeight;
      switch (size) {
        case 'small':
        case 'medium':
          return 'normal';
        case 'full width':
          return 'bold';
        default:
          return 900;
      }
    }};

    width: ${() => {
      switch (size) {
        case 'full width':
          return '100%';
        case 'small':
          return '10rem';
        case 'medium':
          return '12.4rem';
        case 'fit':
          return 'fit-content';
        default:
          return 'auto';
      }
    }};

    height: ${() => {
      switch (size) {
        case 'auto':
          return 'auto';
        case 'fit':
          return 'fit-content';
        default:
          return '3.2rem';
      }
    }};

    padding: ${() => {
      switch (size) {
        case 'small':
          return `0 ${Spacing.x2}rem 0.2rem`;
        case 'medium':
          return `${Spacing.x}rem ${Spacing.x4}rem`;
        case 'fit':
          return `${Spacing.x2}rem ${Spacing.x4}rem`;
        case 'full width':
          return `${Spacing.x}rem ${Spacing.x4}rem`;
        case 'auto':
          return `${Spacing.x}rem ${Spacing.x4}rem`;
      }
    }};

    border: ${() => {
      switch (variant) {
        case 'outline':
          return `solid 1px var(${buttonTheme[variant][colorScheme].border})`;
        default:
          return 'none';
      }
    }};

    background-color: ${() => {
      switch (variant) {
        case 'contained':
          return `var(${buttonTheme[variant][colorScheme].background})`;
        default:
          return 'transparent';
      }
    }};

    color: ${() => `var(${buttonTheme[variant][colorScheme].text})`};

    cursor: ${() => (disabled ? 'auto' : 'pointer')};

    svg {
      fill: ${() => `var(${buttonTheme[variant][colorScheme].icon})`};
    }

    & .spinner {
      border-color: ${() =>
        variant !== 'text' &&
        `var(${buttonBehaviorTheme[variant].spinnerBorder})
        var(${buttonBehaviorTheme[variant].spinnerBorder})
        var(${buttonBehaviorTheme[variant].spinner})
        `};
    }

    &:disabled {
      background-color: ${() =>
        variant != 'text' && `var(${buttonBehaviorTheme[variant].backgroundDisable})`};
      * {
        color: ${() => variant !== 'text' && `var(${buttonBehaviorTheme[variant].textDisable})`};
        fill: ${() => variant !== 'text' && `var(${buttonBehaviorTheme[variant].textDisable})`};
      }
    }

    &:hover {
      filter: ${() => (disabled ? 'none' : 'invert(10%)')};
    }
  `;
};
